import type { ReactNode } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { FormattedDate, FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { HeadlineS, LabelS } from '#src/atoms/typography/index.tsx';
import { Chip } from '#src/atoms/ui/Chip/index.tsx';
import { playerUnavailability } from '#src/lib/glossary.ts';

export const messages = defineMessages({
  playerHeight: {
    defaultMessage: '{height, number} m',
  },
  expectedReturnOn: {
    defaultMessage: 'Expected return on {date}',
  },
});

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--intermediate-unit);
`;

type Props = {
  informations?: { label: MessageDescriptor; value: ReactNode }[];
  injury?: {
    date: Date | null;
    expectedEndDate?: Date | null;
    reason: string | null;
  };
  suspension?: {
    date: Date;
    expectedEndDate?: Date | null;
    reason: string | null;
  };
};

export const Infos = ({ informations, injury, suspension }: Props) => {
  return (
    <Vertical gap={1.5}>
      <HeadlineS as="h2">
        <FormattedMessage defaultMessage="Info" />
      </HeadlineS>
      {!!informations && (
        <Grid>
          {informations.map(({ label, value }, index) => (
            // oxlint-disable-next-line react/no-array-index-key
            <Vertical key={index} gap={0.5}>
              <LabelS as="p" color="var(--c-nd-600)">
                <FormattedMessage {...label} />
              </LabelS>
              {value}
            </Vertical>
          ))}
        </Grid>
      )}
      {!!injury && (
        <Horizontal>
          <Chip size="S" color="red">
            <FormattedMessage {...playerUnavailability.injuryTitle} /> (
            {!!injury.date && (
              <FormattedDate value={injury.date} month="short" day="numeric" />
            )}
            )
          </Chip>
          <LabelS as="p">
            {!!injury.expectedEndDate && (
              <>
                <FormattedMessage
                  {...messages.expectedReturnOn}
                  values={{
                    date: (
                      <FormattedDate
                        value={injury.expectedEndDate}
                        month="short"
                        day="numeric"
                      />
                    ),
                  }}
                />
                {', '}
              </>
            )}
            {injury.reason}
          </LabelS>
        </Horizontal>
      )}
      {!!suspension && (
        <Horizontal>
          <Chip size="S" color="yellow">
            <FormattedMessage {...playerUnavailability.suspendedTitle} /> (
            <FormattedDate
              value={suspension.date}
              month="short"
              day="numeric"
            />
            )
          </Chip>
          <LabelS as="p">
            {!!suspension.expectedEndDate && (
              <>
                <FormattedMessage
                  {...messages.expectedReturnOn}
                  values={{
                    date: (
                      <FormattedDate
                        value={suspension.expectedEndDate}
                        month="short"
                        day="numeric"
                      />
                    ),
                  }}
                />
                {', '}
              </>
            )}
            {suspension.reason}
          </LabelS>
        </Horizontal>
      )}
    </Vertical>
  );
};
